import { CheckCircleIcon } from "@heroicons/react/solid";
import { Transition } from "@remix-run/react/transition";
import { FC, HTMLInputTypeAttribute, useState } from "react";
import { FieldError } from "~/new_shared/users.server";

export interface ChildFormError {
  success: false;
  errors?: FieldError[];
  generalErrorMessage?: string; // if there was a problem with the form as a whole and not just a particular field
}

export interface ChildFormSuccess {
  success: true;
}

export interface FormSuccess extends ChildFormSuccess {
  _action: string;
}
export interface FormError extends ChildFormError {
  _action: string;
}

export type ActionResult = FormSuccess | FormError;
export type ChildActionResult = ChildFormSuccess | ChildFormError;

const findErrorMessageForField = (
  name: string,
  errors: FieldError[] | undefined,
): string | undefined => {
  if (!errors || errors.length == 0) {
    return undefined;
  }
  const matches = errors.filter((fe) => fe.name == name);
  return matches.length > 0 ? matches.map((fe) => fe.errorMessage).join(". ") : undefined;
};

export const CreateFormErrorForField = (name: string, errorMessage: string): ChildFormError => ({
  success: false,
  errors: [{ name, errorMessage }],
});

interface FormItemProps {
  name: string;
  label?: string;
  result: ActionResult | undefined;
  onChange?: (newVal: string) => void;
}

export interface FormTextBoxProps extends FormItemProps {
  placeholder: string | undefined;
  onChange?: (newVal: string) => void;
  type?: HTMLInputTypeAttribute;
  pattern?: string;
  patternErrorMessage?: string;
}

const FormItemWithLabelAndError: FC<FormItemProps> = ({ children, result, name, label }) => {
  const errorMessage = result?.success ? "" : findErrorMessageForField(name, result?.errors);
  return (
    <div className="w-full">
      {label && <label htmlFor={name}>{label}</label>}
      {children}
      <div className="text-red-500">{errorMessage}</div>
    </div>
  );
};

export const FormTextBox: FC<FormTextBoxProps> = (props) => {
  const { type, name, placeholder, onChange, pattern, patternErrorMessage } = props;
  const hasServerErrorMessage = props.result?.success == false;
  return (
    <FormItemWithLabelAndError {...props}>
      <input
        type={type ?? "text"}
        name={name}
        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        placeholder={placeholder}
        onChange={(ev) => (onChange ? onChange(ev.target.value) : null)}
        pattern={pattern}
      />
      {patternErrorMessage && !hasServerErrorMessage && (
        <div className="show-on-invalid text-slate-500 hidden text-sm">
          {/* this is shown before the form is submitted */}
          {patternErrorMessage}
        </div>
      )}
    </FormItemWithLabelAndError>
  );
};

interface FormSelectProps extends FormItemProps {
  onChange?: (newVal: string) => void;
  value: string;
}

export const FormSelect: FC<FormSelectProps> = (props) => {
  const { name, onChange, value, children } = props;
  return (
    <FormItemWithLabelAndError {...props}>
      <select
        name={name}
        onChange={(ev) => (onChange ? onChange(ev.target.value) : null)}
        value={value}
      >
        {children}
      </select>
    </FormItemWithLabelAndError>
  );
};

export const AsyncMap = async <T, Y>(
  coll: T[],
  mapOperation: (t: T) => Promise<Y>,
): Promise<Y[]> => {
  return await Promise.all(coll.map(async (t) => await mapOperation(t)));
};

export type JsonDate = number; // unix time string
export const GetDateFromJsonDate = (jsonDate: JsonDate) => new Date(jsonDate);

export const GetJsonDateFromDate = (date: Date) => date.valueOf();

export const ShowWorkingIfSubmitting: FC<{
  transition: Transition;
  onlyIf?: boolean; // only show working if this condition is true
}> = (props) =>
  props.transition.state == "submitting" && (props.onlyIf ?? true) ? (
    <>working...</>
  ) : (
    <>{props.children}</>
  );

// I bet there's a bug here due to lag in reading and setting state...
/*
this is a state machine, so here's what's happening:

page first load: 
  state: idle
  justSubmitted: false
  onlyIf: false

another action: 
  state: loading/submitting/idle
  justSubmitted: false
  onlyIf: false

this action submitting:
  state: loading/submitting
  justSubmitted: false OR true, => true
  onlyIf: true  

this action submitted:
  state: idle
  justSubmitted: true
  onlyIf: false

another action after this action: 
  state: loading/submitted
  onlyIf: false
  justSubmitted: true OR false => false

*/
const emptyEl = <></>;
export const SubmissionStatus: FC<{
  transition: Transition;
  onlyIf?: boolean; // only show working if this condition is true
}> = ({ transition, onlyIf }) => {
  const [justSubmitted, setJustSubmitted] = useState(false);

  if (!onlyIf) {
    if (!justSubmitted) {
      return emptyEl;
    }
    if (transition.state == "submitting" || transition.state == "loading") {
      // another action is submitting on this page now
      // so we are no longer the last action on the page
      if (justSubmitted) {
        setJustSubmitted(false);
      }
      // This is not strictly necessary (we will re-render due to the setState)
      return emptyEl;
    }

    // we were the last action to be submitted
    // span is needed to force the icon to be displayed inline
    return (
      <span>
        <CheckCircleIcon className="w-4 h-4 inline" />
      </span>
    );
  }

  // we are the current action being submitted
  if (!justSubmitted) {
    setJustSubmitted(true);
  }
  return <>Working...</>;
};

/*
idea for future: 
  when transition != idle, set *all* form items to disabled
    can we do this by having some sort of parent control (css/tailwind group?) that sets a context/disabled state for everything under it? 
  together with SubmissionStatus control (with spinner), this shows it's working  
  
*/
